import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Copy, FileSpreadsheet, Pencil, Play, Plus, Search, TerminalSquare, Trash2 } from 'lucide-react';
import { reportsAPI } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import { useAppStore } from '../../stores/useAppStore';
import { canManageReports } from '../../utils/accessControl';
import { formatDateTime } from '../../utils/dayjs';
import { Button, Input } from '../ui';
import { extractApiError, summarise } from './lib/apiErrors';
import { useReportCatalog } from './useReportCatalog';

const TH = 'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';

function KindBadge({ kind }) {
  const cls = kind === 'sql' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800';
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{kind === 'sql' ? 'SQL' : 'Builder'}</span>;
}

const ReportList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addNotification } = useAppStore();
  const isAdmin = canManageReports(user);
  const { datasetByKey, sqlModeEnabled } = useReportCatalog();

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await reportsAPI.list(isAdmin ? { include_inactive: true } : {});
      setReports(res.data || []);
    } catch (err) {
      setError(summarise(await extractApiError(err)));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return reports;
    return reports.filter((r) => `${r.Name} ${r.Description || ''}`.toLowerCase().includes(q));
  }, [reports, query]);

  const duplicate = async (r) => {
    setBusyId(r.ID);
    try {
      const full = (await reportsAPI.get(r.ID)).data;
      const created = await reportsAPI.create({
        Name: `${full.Name} (copy)`,
        Description: full.Description,
        Kind: full.Kind,
        Definition: full.Definition,
        SqlText: full.SqlText,
        Parameters: full.Parameters,
        IsActive: true,
      });
      addNotification({ type: 'success', message: `Duplicated as "${created.data.Name}"` });
      navigate(`/reports/${created.data.ID}/edit`);
    } catch (err) {
      addNotification({ type: 'error', message: summarise(await extractApiError(err)) });
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete report "${r.Name}"? This cannot be undone.`)) return;
    setBusyId(r.ID);
    try {
      await reportsAPI.remove(r.ID);
      setReports((list) => list.filter((x) => x.ID !== r.ID));
      addNotification({ type: 'success', message: 'Report deleted' });
    } catch (err) {
      addNotification({ type: 'error', message: summarise(await extractApiError(err)) });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-sm text-gray-500">Saved reports you can run and export.</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            {sqlModeEnabled && (
              <Button variant="secondary" onClick={() => navigate('/reports/sql')}>
                <TerminalSquare className="w-4 h-4 mr-2" /> SQL Workbench
              </Button>
            )}
            <Button onClick={() => navigate('/reports/new')}>
              <Plus className="w-4 h-4 mr-2" /> New Report
            </Button>
          </div>
        )}
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input className="pl-9" placeholder="Search reports…" value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className={TH}>Name</th>
              <th className={TH}>Kind</th>
              <th className={TH}>Dataset</th>
              <th className={TH}>Updated</th>
              <th className={`${TH} text-right`}>Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center">
                  <FileSpreadsheet className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                  {reports.length === 0 ? (
                    <>
                      <p className="text-sm font-medium text-gray-900">No reports yet</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {isAdmin ? (
                          <>
                            <Link to="/reports/new" className="text-primary-600 hover:underline">
                              Create your first report
                            </Link>{' '}
                            with the guided builder.
                          </>
                        ) : (
                          'Ask an admin to create reports for you.'
                        )}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">No reports match “{query}”.</p>
                  )}
                </td>
              </tr>
            )}
            {!loading &&
              filtered.map((r) => (
                <tr key={r.ID} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/reports/${r.ID}`)}>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                      {r.Name}
                      {!r.IsActive && <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">Inactive</span>}
                    </div>
                    {r.Description && <div className="text-xs text-gray-500 truncate max-w-md">{r.Description}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <KindBadge kind={r.Kind} />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{r.Dataset ? datasetByKey.get(r.Dataset)?.label || r.Dataset : '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{formatDateTime(r.UpdatedDate || r.CreatedDate)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/reports/${r.ID}`)} title="Run">
                        <Play className="w-4 h-4" />
                      </Button>
                      {isAdmin && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => navigate(`/reports/${r.ID}/edit`)} title="Edit">
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" disabled={busyId === r.ID} onClick={() => duplicate(r)} title="Duplicate">
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" disabled={busyId === r.ID} onClick={() => remove(r)} title="Delete" className="text-red-600 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReportList;
